import React, { RefObject, memo, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import moment, { Moment } from "moment";
import FsLightbox from "fslightbox-react";
import { useSelector } from "react-redux";

import styles from "./message-item.module.scss";
import { MessageItemProps } from "./message-item.types";

import { BASE_URL } from "~/app/utils/api-fetch";
import { selectCurrentUserId } from "~/app/selectors/messages";

const MessageItem: React.FC<MessageItemProps> = memo((props: MessageItemProps) => {
  const [lightboxController, setLightboxController]: [
    { toggler: boolean; slide: number },
    React.Dispatch<React.SetStateAction<{ toggler: boolean; slide: number }>>
  ] = useState<{
    toggler: boolean;
    slide: number;
  }>({
    toggler: false,
    slide: 1
  });

  const userId: string | undefined = useSelector(selectCurrentUserId);

  const ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && userId !== props.message.user?.id) {
      const observer: IntersectionObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              props.handleMessageStatusReadUpdate(props.message);
            }
          });
        },
        { threshold: 1.0 }
      );

      observer.observe(ref.current);
      return (): void => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    }
  }, [props]);

  const openLightboxOnSlide: (number: number) => void = (number: number): void => {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: number
    });
  };

  const today: Moment = moment().endOf("day");
  const dateMessage: Moment = moment(props.message.created_at);
  const dateFormatMessage: string =
    moment(dateMessage).diff(today, "days") === 0
      ? moment(dateMessage).format("HH:mm")
      : moment(dateMessage).format("DD.MM.YY");

  const images: string[] = props.message.media ? props.message.media?.split(",") : [];

  const imagesAddedUrl: string[] = images.map(image =>
    !image.includes("http") ? `${BASE_URL}/images/${image}` : image
  );

  const countMedias: number = images.length;
  return (
    <div className={classNames(styles.messageItem, userId === props.message.user?.id ? styles.own : "")} ref={ref}>
      <div className={styles.messageContentWrap}>
        <div className={styles.mediaContentWrap}>
          {imagesAddedUrl.map((media, index) => {
            return (
              <div
                key={index}
                style={{
                  width: countMedias == 1 ? "40rem" : "19.8rem",
                  height: countMedias == 1 ? "40rem" : "19.8rem",
                  backgroundImage: `url(${media})`
                }}
                onClick={(): void => openLightboxOnSlide(index + 1)}
              ></div>
            );
          })}
        </div>
        <FsLightbox toggler={lightboxController.toggler} sources={imagesAddedUrl} slide={lightboxController.slide} />
        <div className={styles.messageContent}>
          <p className={styles.text}>
            {props.message.message}
            <span className={styles.messageMeta}>{dateFormatMessage}</span>
          </p>
        </div>
      </div>
    </div>
  );
});

MessageItem.displayName = "MessageItem";

export default MessageItem;
